<template>
  <Modal>
    <Modal.Title>{{ confirm.title }}</Modal.Title>
    <Modal.Body>
      <p class="text-[14px] leading-[22px] text-gray-800">
        <template v-if="hasOutstandingBalance"
          >By continuing, I authorize Dacr to charge my payment method for the
          <span class="font-medium"
            >outstanding balance of {{ outstandingBalance }}</span
          >{{ confirm.bodyAfterBalance }}</template
        >
        <template v-else>{{ confirm.bodyNoBalance }}</template>
      </p>

      <div v-if="showCheckbox" class="mt-4 flex items-start gap-2">
        <Checkbox v-model="agreed" class="mt-px shrink-0" />
        <p
          class="cursor-pointer text-[12px] leading-[17px] text-gray-500"
          @click="agreed = !agreed"
        >
          <template v-if="hasOutstandingBalance"
            >I authorize this charge, confirm it is final and non-refundable,
            and acknowledge my continued acceptance of the
            <a href="/legal/terms" target="_blank" class="text-link" @click.stop
              >Dacr Terms</a
            >,
            <a
              href="/legal/privacy"
              target="_blank"
              class="text-link"
              @click.stop
              >Privacy Policy</a
            >, and
            <a
              href="/legal/payments"
              target="_blank"
              class="text-link"
              @click.stop
              >Payments Policy</a
            >.</template
          >
          <template v-else>{{ confirm.checkboxNoBalance }}</template>
        </p>
      </div>
    </Modal.Body>
    <Modal.Footer layout="vertical">
      <Button
        variant="danger"
        :size="isMobile ? 'lg' : 'md'"
        :disabled="!canSubmit"
        :loading="submitting"
        @click="submit"
      >
        {{ primaryLabel }}
      </Button>
      <Button
        variant="stroke"
        :size="isMobile ? 'lg' : 'md'"
        :disabled="submitting"
        @click="handleKeep"
      >
        {{ confirm.secondary }}
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Modal, Button, Checkbox } from '@dacr/library'

import { useDevice } from '@/shared/composables/useDevice'
import { closeAllModals } from '@/shared/dialogs/modals'
import { closeAllSheets } from '@/shared/dialogs/sheets'

import { closureContent, type ClosureMode } from './content'
import { useOutstandingBalance } from './useOutstandingBalance'
import { useAccountClosure } from './useAccountClosure'

const props = defineProps<{ mode: ClosureMode }>()

const { isMobile } = useDevice()
const { hasOutstandingBalance, outstandingBalance } = useOutstandingBalance()
const { submitting, submit } = useAccountClosure(props.mode)

const confirm = computed(() => closureContent[props.mode].confirm)

const agreed = ref(false)
const showCheckbox = computed(
  () => props.mode === 'delete' || hasOutstandingBalance.value
)
const canSubmit = computed(() => !showCheckbox.value || agreed.value)

const primaryLabel = computed(() =>
  hasOutstandingBalance.value
    ? confirm.value.primaryWithBalance
    : confirm.value.primaryNoBalance
)

const handleKeep = () => {
  closeAllModals()
  closeAllSheets()
}
</script>
